import { useLocation } from 'react-router'
import { HeaderMarkup } from '@/components/chrome/HeaderMarkup'
import { FooterMarkup } from '@/components/chrome/FooterMarkup'
import { PromoBarMarkup, type PromoVariant } from '@/components/chrome/PromoBarMarkup'
import { useChromeNav } from '@/components/chrome/useChromeNav'
import { useHeaderContrast } from '@/components/chrome/useHeaderContrast'
import { usePrefetchOnIntent } from '@/components/chrome/usePrefetchOnIntent'
import { StudioFooter } from '@/components/chrome/StudioFooter'
import { StudioStickyCta } from '@/components/chrome/StudioStickyCta'
import { JewelersHeader } from '@/components/chrome/JewelersHeader'

/**
 * The site's shared chrome, rendered once around every ported page.
 *
 * Before Phase 3 each of the 59 ported pages carried its own inline copy of
 * all of this — 2.94 MB, 68.8% of the entire page corpus — of which 43 headers
 * and 47 footers were byte-identical. Every remaining difference turned out to
 * be derivable:
 *
 *   - `w--current` / `aria-current` on the matching link, now from NavLink
 *   - the `dark` header class, the sticky offset, the header background and
 *     every header SVG fill, all of which WebflowBinder already computes at
 *     runtime from the background of the section under the header
 *
 * Only the promo strip is real content variance: 18 routes carry one, and the
 * capture holds two different campaigns.
 *
 * Landmarks are new. The capture wrapped the header and footer in plain
 * `<div>`s and had no `<main>` at all on 58 of 59 routes, so a screen reader
 * got no structure to navigate by.
 *
 * Phase 6 replaced the nav's behaviour: `useChromeNav` opens the dropdowns and
 * the mobile sheet in React, so jQuery and Webflow's 331 KB engine are gone.
 * The markup is unchanged — it is what five phases of `dom-diff` have held to
 * the capture — and the keyboard behaviour it never had is now there.
 */
export function SiteChrome({
  promo,
  children,
}: {
  /** Which promo campaign this route ran, if any. */
  promo?: PromoVariant | null
  children: React.ReactNode
}) {
  const { pathname } = useLocation()
  useChromeNav(pathname)
  usePrefetchOnIntent()

  /*
   * The header's contrast pass belongs here, with the header.
   *
   * It used to ride along in `WebflowBinder`, which only two route wrappers
   * mount — `DocumentRoute` and `PortedPage`. Every other route (the
   * spec-driven product pages, `/outbound`, `/roi-calculator`) rendered this
   * header with nothing measuring what was behind it, so the nav kept the
   * `--header-ink` fallback and came out dark over a dark hero. `SiteChrome`
   * renders the header on all of them, so it is the one place that cannot
   * disagree with itself.
   */
  useHeaderContrast(pathname)

  // /studio keeps its own footer and the phone sticky CTA. Since the Oct 19,
  // 2026 public launch it shares the site header: /studio is linked from the
  // main nav, the banner and the footer, and its old stripped header made it
  // look like a different site.
  const path = pathname.replace(/\/$/, '')
  const studio = path === '/studio'
  // The jewelers landing page swaps in a reduced, pinned header that carries the
  // action at every width, so it needs no sticky bar. It keeps the site footer
  // and every legal link.
  const jewelers = path === '/studio-for-jewelers'

  return (
    <>
      {promo ? <PromoBarMarkup variant={promo} /> : null}
      {jewelers ? <JewelersHeader /> : <HeaderMarkup />}
      <main>{children}</main>
      {studio ? <StudioFooter /> : <FooterMarkup />}
      {studio && <StudioStickyCta />}
    </>
  )
}
